/**
 * «Добавить» — the single funnel into creating content (handoff §2, §19.1;
 * ux_scenario §3).
 *
 * A bottom sheet on mobile and a popover anchored under its trigger on desktop.
 * Home and Library use this one implementation, so the two surfaces cannot drift
 * on what "add" offers or how it is described. Each row routes into an existing
 * flow; no flow is reimplemented here. The two text rows start the text creation
 * flow (texts, ADR-115) as «Слова из текста» and «Новый текст».
 *
 * Retiring or repointing the older text-import entries (the deck screen's
 * import, share target, the lookup palette's long-text redirect, /text-import)
 * is a later decision (ROADMAP, texts); this sheet no longer offers them.
 */
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Compass, FileText, Layers, ScanText, Search, Table2, X } from 'lucide-react'
import { useLookupPaletteStore } from '../../stores/lookupPaletteStore'
import { useTextFlowStore, type FlowEntry } from '../../stores/textFlowStore'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import { useExclusiveOverlay } from '../../hooks/useExclusiveOverlay'
import { libraryHref } from '../../utils/libraryAddress'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'

interface Props {
  open: boolean
  onClose: () => void
  /** Parent callback: show the DeckCreateModal */
  onCreateDeck: () => void
  /** Parent callback: show the CSVImportModal */
  onImportCSV: () => void
  /** Overrides where «Каталог колод» goes. Defaults to Library → Catalog. */
  onFindDecks?: () => void
  /** The control that opened the sheet. On desktop the popover hangs under it
   *  and Esc returns focus to it; without one the popover is centred. */
  anchorRef?: React.RefObject<HTMLElement | null>
}

const POPOVER_WIDTH = 400
const POPOVER_GAP = 8

const SHEET_CSS = `
.add-sheet-row{background:none;border:none;cursor:pointer;text-align:left;width:100%;display:flex;align-items:center;color:var(--text-primary);font-family:var(--font-ui)}
.add-sheet-row:hover,.add-sheet-row:active,.add-sheet-row:focus-visible{background:var(--accent-soft);outline:none}
`
// The scrim's classes (.add-sheet-scrim, --desktop) are global, in index.css:
// the flow's LeaveConfirm and DeckPickerSheet use them while this sheet is closed.

export default function AddContentSheet({
  open, onClose, onCreateDeck, onImportCSV, onFindDecks, anchorRef,
}: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const prefersReducedMotion = useReducedMotion()
  const { openAsSheet } = useLookupPaletteStore()
  const { tr } = useLocale()
  const ac = tr.addContent
  const panelRef = useRef<HTMLDivElement>(null)
  // "closed via Android back" vs "closed programmatically"
  const closedViaBackRef = useRef(false)
  // ADR-106 P3: no top-level swipe while the sheet is open.
  useSwipeNavBlock(open, 'add-content-sheet')
  // Opening the sheet closes any menu or palette that is open, so it never sits under one.
  useExclusiveOverlay(open, onClose)

  const isMobile =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  const noAnim = Boolean(prefersReducedMotion)

  // ── Desktop popover position, from the trigger ────────────────────────────
  // Set on the element before paint rather than held in state: it is a
  // measurement of another element, not something React renders from.
  useLayoutEffect(() => {
    if (!open || isMobile) return
    const anchor = anchorRef?.current
    const panel = panelRef.current
    if (!anchor || !panel) return
    const rect = anchor.getBoundingClientRect()
    panel.style.top = `${rect.bottom + POPOVER_GAP}px`
    panel.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`
  }, [open, isMobile, anchorRef])

  // ── Esc closes; desktop returns focus to the trigger ─────────────────────
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      onClose()
      if (!isMobile) anchorRef?.current?.focus()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose, isMobile, anchorRef])

  // The close callback is read through a ref: parents pass an inline arrow, a new
  // function on every render, and an effect that depended on it would tear down and
  // re-push the history sentinel whenever the parent re-rendered with the sheet open.
  // Tearing down pops the entry with history.back(), whose popstate then closed the
  // freshly opened sheet (seen when a menu closing re-rendered Library under it).
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
  })

  // ── Android back: a sentinel history entry so back closes the sheet ──────
  useEffect(() => {
    if (!open || !isMobile) return
    closedViaBackRef.current = false
    history.pushState({ addContentSheetOpen: true }, '')
    const handler = () => {
      closedViaBackRef.current = true
      onCloseRef.current()
    }
    window.addEventListener('popstate', handler)
    return () => {
      window.removeEventListener('popstate', handler)
      if (
        !closedViaBackRef.current &&
        (history.state as { addContentSheetOpen?: boolean } | null)?.addContentSheetOpen
      ) {
        history.back()
      }
    }
  }, [open, isMobile])

  // ── Desktop: focus the first row, ↑/↓ move between rows ──────────────────
  useEffect(() => {
    if (!open || isMobile) return
    const t = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLButtonElement>('.add-sheet-row')?.focus()
    }, 0)
    return () => window.clearTimeout(t)
  }, [open, isMobile])

  function onPanelKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const rows = Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('.add-sheet-row') ?? [])
    if (rows.length === 0) return
    e.preventDefault()
    const at = rows.indexOf(document.activeElement as HTMLButtonElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % rows.length : (at - 1 + rows.length) % rows.length
    rows[next].focus()
  }

  function go(fn: () => void) {
    return () => { onClose(); fn() }
  }

  function startFlow(entry: FlowEntry) {
    useTextFlowStore.getState().start(entry, location.pathname + location.search)
    navigate('/texts/new')
  }

  const sections: Array<{
    eyebrow: string
    rows: Array<{ icon: React.ReactNode; label: string; desc: string; onClick: () => void; testId: string }>
  }> = [
    {
      eyebrow: ac.eyebrowWords,
      rows: [
        { icon: <Search size={20} />, ...ac.lookup, onClick: go(() => openAsSheet()), testId: 'sheet-lookup' },
        { icon: <ScanText size={20} />, ...ac.fromText, onClick: go(() => startFlow('words')), testId: 'sheet-text-words' },
        { icon: <Table2 size={20} />, ...ac.fromTable, onClick: go(onImportCSV), testId: 'sheet-csv' },
      ],
    },
    {
      eyebrow: ac.eyebrowCreate,
      rows: [
        { icon: <Layers size={20} />, ...ac.newDeck, onClick: go(onCreateDeck), testId: 'sheet-create-deck' },
        { icon: <FileText size={20} />, ...ac.newText, onClick: go(() => startFlow('essay')), testId: 'sheet-text-essay' },
      ],
    },
    {
      eyebrow: ac.eyebrowFind,
      rows: [
        {
          icon: <Compass size={20} />, ...ac.catalog,
          onClick: go(onFindDecks ?? (() => navigate(libraryHref('catalog', 'decks')))),
          testId: 'sheet-explore',
        },
      ],
    },
  ]

  const tile = isMobile ? 40 : 36
  const rowStyle: React.CSSProperties = {
    gap: 14,
    padding: isMobile ? '8px 20px' : '6px 20px',
    minHeight: isMobile ? 60 : 56,
  }

  const panelMotion = isMobile
    ? {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        transition: noAnim
          ? { duration: 0 }
          : { type: 'tween' as const, duration: 0.22, ease: 'easeOut' as const },
      }
    : {
        initial: { opacity: 0, scale: 0.96 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.96 },
        transition: noAnim ? { duration: 0 } : { duration: 0.15 },
      }

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        background: 'var(--bg-surface)',
        borderRadius: '16px 16px 0 0',
        boxShadow: 'var(--shadow-float)',
        overflow: 'hidden',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
      }
    : {
        position: 'fixed',
        // With a trigger the layout effect above places it; without one it is centred.
        top: '15vh',
        ...(anchorRef ? {} : { left: '50%', transform: 'translateX(-50%)' }),
        width: '100%',
        maxWidth: POPOVER_WIDTH,
        zIndex: 60,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-float)',
        overflow: 'hidden',
        padding: '8px 0',
      }

  return (
    <AnimatePresence>
      {open && (
        <>
          <style>{SHEET_CSS}</style>
          {/* Scrim on mobile; a transparent click-away layer on desktop (§19.1). */}
          <motion.div
            key="add-content-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: noAnim ? 0 : 0.15 }}
            onClick={onClose}
            className={`add-sheet-scrim${isMobile ? '' : ' add-sheet-scrim--desktop'}`}
            style={{ position: 'fixed', inset: 0, zIndex: 50 }}
          />

          <motion.div
            key="add-content-panel"
            ref={panelRef}
            role="dialog"
            aria-label={ac.title}
            aria-modal="true"
            // Rows are controls: no OS selection or Touch to Search on their labels.
            className={CONTROL_SURFACE}
            onContextMenu={suppressTouchContextMenu}
            style={panelStyle}
            onKeyDown={onPanelKeyDown}
            {...panelMotion}
          >
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 0' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)' }} />
              </div>
            )}

            {isMobile && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px 0 20px',
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  {ac.title}
                </h2>
                <button
                  onClick={onClose}
                  aria-label={ac.close}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 44,
                    minHeight: 44,
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            )}

            {sections.map((section, i) => (
              <div key={section.eyebrow}>
                {i > 0 && <div style={{ height: 1, background: 'var(--border)', margin: '8px 20px 0' }} />}
                <p
                  style={{
                    margin: 0,
                    padding: isMobile ? `${i === 0 ? 8 : 16}px 20px 4px` : '10px 20px 6px',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  {section.eyebrow}
                </p>
                {section.rows.map((row) => (
                  <button
                    key={row.testId}
                    className="add-sheet-row"
                    data-testid={row.testId}
                    onClick={row.onClick}
                    style={rowStyle}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: tile,
                        height: tile,
                        borderRadius: 8,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--accent-soft)',
                        color: 'var(--accent)',
                      }}
                    >
                      {row.icon}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ fontSize: isMobile ? 15 : 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {row.label}
                      </span>
                      <span style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
                        {row.desc}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
